import React, { createContext, useState, useEffect, useContext } from 'react';
import { db, supabase } from '../api/supabase';

const AuthContext = createContext();

// Custom hook to use the auth context
export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

const AuthProvider = ({ children }) => {
    const [auth, setAuth] = useState(null);
    const [loading, setLoading] = useState(true);
    const [userProfile, setUserProfile] = useState(null);
    const [sessionValid, setSessionValid] = useState(false);
    
    const debug = true; // Set this to true to enable debugging

    // Check for existing session on component mount
    useEffect(() => {
        const checkUser = async () => {
            try {
                if (debug) console.log('Checking for existing session...');
                setLoading(true);
                
                // Get the current session
                const { data, error } = await supabase.auth.getSession();
                
                if (error) {
                    if (debug) console.error('Error getting session:', error);
                    setAuth(null);
                    setUserProfile(null);
                    setSessionValid(false);
                    setLoading(false);
                    return;
                }
                
                const session = data?.session;
                const user = session?.user;
                
                if (debug) console.log('Session check result:', session ? 'Session found' : 'No session');
                
                if (!user) {
                    if (debug) console.log('No user in session');
                    setAuth(null);
                    setUserProfile(null);
                    setSessionValid(false);
                    setLoading(false);
                    return;
                }
                
                if (debug) console.log('User from session:', user.id);
                
                // Verify the session is valid by making an authenticated request
                try {
                    const { error: authError } = await supabase.auth.getUser();
                    
                    if (authError) {
                        if (debug) console.error('Session validation failed:', authError);
                        // Session is invalid, clear everything
                        await resetSession();
                        setLoading(false);
                        return;
                    }
                    
                    if (debug) console.log('Session validated successfully');
                    setAuth(user);
                    
                    // Fetch user profile
                    const profile = await fetchUserProfile(user.id);
                    
                    if (!profile) {
                        if (debug) console.error('User profile not found, session considered invalid');
                        // If profile doesn't exist, session is considered invalid
                        await resetSession();
                        setLoading(false);
                        return;
                    }
                    
                    // Session and profile are valid
                    setSessionValid(true);
                    
                } catch (validationError) {
                    if (debug) console.error('Error validating session:', validationError);
                    await resetSession();
                }
            } catch (error) {
                if (debug) console.error('Error checking user session:', error);
                setAuth(null);
                setUserProfile(null);
                setSessionValid(false);
            } finally {
                setLoading(false);
            }
        };
        
        checkUser();
        
        // Set up auth state change listener
        const { data: authListener } = supabase.auth.onAuthStateChange(async (event, session) => {
            if (debug) console.log('Auth state changed:', event, session);
            
            if (event === 'SIGNED_IN') {
                const user = session?.user;
                if (user) {
                    if (debug) console.log('User signed in:', user.id);
                    setAuth(user);
                    const profile = await fetchUserProfile(user.id);
                    setSessionValid(!!profile);
                }
            } else if (event === 'SIGNED_OUT') {
                if (debug) console.log('User signed out');
                setAuth(null);
                setUserProfile(null);
                setSessionValid(false);
            } else if (event === 'TOKEN_REFRESHED') {
                const user = session?.user;
                if (user) {
                    if (debug) console.log('Token refreshed for user:', user.id);
                    setAuth(user);
                    // Verify session is still valid after token refresh
                    try {
                        const { error: authError } = await supabase.auth.getUser();
                        if (authError) {
                            if (debug) console.error('Session invalid after token refresh:', authError);
                            await resetSession();
                        } else {
                            setSessionValid(true);
                        }
                    } catch (error) {
                        if (debug) console.error('Error validating refreshed token:', error);
                        await resetSession();
                    }
                }
            } else if (event === 'USER_UPDATED') {
                const user = session?.user;
                if (user) {
                    if (debug) console.log('User updated:', user.id);
                    setAuth(user);
                    const profile = await fetchUserProfile(user.id);
                    setSessionValid(!!profile);
                }
            }
        });
        
        // Clean up subscription on unmount
        return () => {
            if (authListener && authListener.subscription) {
                authListener.subscription.unsubscribe();
            }
        };
    }, [debug]); // Add debug as a dependency

    // Fetch user profile from the unified users table
    const fetchUserProfile = async (userId) => {
        try {
            if (debug) console.log('Fetching user profile for ID:', userId);
            
            // Get user profile from the unified users table
            const { data: userData, error: userError } = await db.getUserByAuthId(userId);
            
            if (userError) {
                if (debug) console.error('Error fetching user profile:', userError);
                return null;
            }
            
            if (userData) {
                if (debug) console.log('User profile found:', userData);
                setUserProfile({ 
                    ...userData, 
                    userType: userData.user_type // For backward compatibility
                });
                return userData;
            }
            
            if (debug) console.warn('No user profile found for user ID:', userId);
            return null;
        } catch (error) {
            if (debug) console.error('Error fetching user profile:', error);
            return null;
        }
    };

    const login = async (email, password) => {
        try {
            if (debug) console.log('Attempting login...');
            
            // Clear any existing auth state first
            setAuth(null);
            setUserProfile(null);
            setSessionValid(false);
            
            // Call the Supabase signIn method
            const { data, error } = await supabase.auth.signInWithPassword({ 
                email, 
                password 
            });
            
            if (error) {
                if (debug) console.error('Login Failed:', error);
                return { success: false, error };
            }
            
            const user = data?.user;
            const session = data?.session;
            
            if (!user || !session) {
                if (debug) console.error('Login Failed: No user or session returned');
                return { success: false, error: new Error('Authentication failed') };
            }
            
            if (debug) console.log('Login successful, user authenticated:', user.id);
            
            // Update auth state
            setAuth(user);
            
            // Fetch user profile
            const profile = await fetchUserProfile(user.id);
            
            if (!profile) {
                if (debug) console.error('Login successful but profile not found');
                return { success: false, error: new Error('User profile not found') };
            }
            
            setSessionValid(true);
            return { success: true, user };
        } catch (error) {
            if (debug) console.error('Login Failed:', error);
            return { success: false, error };
        }
    };

    const register = async (email, password, userData, userType) => {
        try {
            if (debug) console.log('Attempting registration...');
            
            const { data, error } = await supabase.auth.signUp({
                email,
                password,
                options: {
                    data: {
                        user_type: userType,
                        ...userData
                    }
                }
            });
            
            if (error) {
                if (debug) console.error('Registration Failed:', error);
                return { success: false, error };
            }
            
            const user = data?.user;
            
            if (!user) {
                if (debug) console.error('Registration Failed: No user returned');
                return { success: false, error: new Error('Registration failed') };
            }
            
            if (debug) console.log('Registration successful:', user);
            
            // Create user profile in the unified users table
            const profileData = {
                auth_id: user.id,
                email,
                user_type: userType,
                ...userData
            };
            
            const profileResult = await db.createUser(profileData);
            
            if (profileResult?.error) {
                if (debug) console.error('Profile creation failed:', profileResult.error);
            } else {
                if (debug) console.log('Profile created:', profileResult?.data);
            }
            
            // Check if email confirmation is required
            if (user.confirmation_sent_at) {
                return { 
                    success: true, 
                    user,
                    emailConfirmationRequired: true,
                    message: 'Registration successful! Please check your email to confirm your account.'
                };
            }
            
            return { success: true, user };
        } catch (error) {
            if (debug) console.error('Registration Failed:', error);
            return { success: false, error };
        }
    };

    const logout = async () => {
        try {
            if (debug) console.log('Attempting logout...');
            
            // Call Supabase signOut first to clear the session
            const { error } = await supabase.auth.signOut();
            
            if (error) {
                if (debug) console.error('Logout failed:', error);
                throw error;
            }
            
            // Then clear React state
            setAuth(null);
            setUserProfile(null);
            
            if (debug) console.log('Logout successful');
            
            return { success: true };
        } catch (error) {
            if (debug) console.error('Logout process error:', error);
            
            // Even if there's an error, still clear the local state
            setAuth(null);
            setUserProfile(null);
            
            return { success: false, error };
        }
    };

    const resetPassword = async (email) => {
        try {
            if (debug) console.log('Sending password reset email...');
            const { error } = await supabase.auth.resetPasswordForEmail(email, {
                redirectTo: `${window.location.origin}/auth/callback?type=recovery`,
            });
            
            if (error) {
                if (debug) console.error('Password reset failed:', error);
                return { success: false, error };
            }
            
            if (debug) console.log('Password reset email sent');
            return { success: true };
        } catch (error) {
            if (debug) console.error('Password reset failed:', error);
            return { success: false, error };
        }
    };

    const updatePassword = async (newPassword) => {
        try {
            if (debug) console.log('Updating password...');
            const { error } = await supabase.auth.updateUser({
                password: newPassword
            });
            
            if (error) {
                if (debug) console.error('Password update failed:', error);
                return { success: false, error };
            }
            
            if (debug) console.log('Password updated successfully');
            return { success: true };
        } catch (error) {
            if (debug) console.error('Password update failed:', error);
            return { success: false, error };
        }
    };

    const updateProfile = async (profileData) => {
        try {
            if (!auth || !userProfile) {
                throw new Error('User not authenticated or profile not found');
            }
            
            if (debug) console.log('Updating user profile...');
            
            const result = await db.updateUser(userProfile.id, profileData);
            
            if (result.error) {
                throw result.error;
            }
            
            // Update local state
            setUserProfile({ ...userProfile, ...profileData });
            
            if (debug) console.log('Profile updated successfully');
            return { success: true };
        } catch (error) {
            if (debug) console.error('Profile update failed:', error);
            return { success: false, error };
        }
    };

    // Function to reset the session completely
    const resetSession = async () => {
        console.log('Resetting session completely...');
        
        try {
            // Sign out from Supabase first
            try {
                await supabase.auth.signOut();
                console.log('Supabase signOut completed during reset');
            } catch (signOutError) {
                console.error('Error during Supabase signOut in reset:', signOutError);
            }
            
            // Reset auth state
            setAuth(null);
            setUserProfile(null);
            setSessionValid(false);
            
            // Clear localStorage
            try {
                localStorage.clear();
                console.log('localStorage cleared');
            } catch (storageError) {
                console.error('Error clearing localStorage:', storageError);
            }
            
            // Clear cookies
            try {
                document.cookie.split(";").forEach(function(c) {
                    const cookieName = c.replace(/^ +/, "").replace(/=.*/, "");
                    document.cookie = cookieName + "=;expires=" + new Date().toUTCString() + ";path=/";
                });
                console.log('Cookies cleared');
            } catch (cookieError) {
                console.error('Error clearing cookies:', cookieError);
            }
            
            console.log('Session reset completed successfully');
            return { success: true, message: 'Session reset successful' };
        } catch (error) {
            console.error('Error during session reset:', error);
            return { success: false, message: 'Error resetting session' };
        }
    };

    return (
        <AuthContext.Provider
            value={{
                auth,
                userProfile,
                loading,
                sessionValid,
                login,
                register,
                logout,
                resetPassword,
                updatePassword,
                updateProfile,
                isAdmin: userProfile?.user_type === 'admin',
                isSupport: userProfile?.user_type === 'support',
                isBuyer: userProfile?.user_type === 'buyer' || userProfile?.user_type === 'both',
                isSeller: userProfile?.user_type === 'seller' || userProfile?.user_type === 'both',
                fetchUserProfile,
                resetSession
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};

export { AuthContext, AuthProvider };